/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useStripeProducts } from "queries/agency";
import { useAccountsByAgency, useDeleteAccount, accountKeys } from "queries/accounts";
import EditModal from "../../EditModal";
import { useAgencyInfo } from "context/agency";
import { Link } from "react-router-dom";

const { GET } = ReqMethods;

export default function dataTableData(params = '') {
  const request = useRequest();
  const [agency] = useAgencyInfo();
  const queryClient = useQueryClient();

  const { data: products = [] } = useStripeProducts();

  // "app" domain - not an Accounts hook (another fork owns that territory),
  // so inlined directly via useRequest() rather than centralized.
  const { data: apps = [] } = useQuery({
    queryKey: ["apps-list"],
    queryFn: async () => {
      const { response } = await request(GET, "/app", { query: "", toaster: false });
      if (!response) return [];
      return response.data.map((element) => ({ label: element.name, value: element._id }));
    },
  });

  // Only fetched when called with no params (AccountDetail.js) - mirrors the
  // old onLoad()'s `if (params === '') getOffers()` branch. Shares the
  // "accounts" resource key prefix so any account mutation elsewhere
  // (create/update/delete) invalidates this list too.
  const { data: List = [], isLoading: accountsLoading } = useAccountsByAgency(agency._id, {
    enabled: params === '' && !!agency._id,
  });

  const loader = params === '' ? accountsLoading : false;

  const handleRefresh = () => queryClient.invalidateQueries({ queryKey: accountKeys.all });

  const { mutateAsync: deleteAccountRequest } = useDeleteAccount();

  const handleDelete = async (id) => {
    const { response } = await deleteAccountRequest(`_id=${id}`);
    if (response) handleRefresh();
  };

  return {
    products: products,
    loader: loader,
    apps: apps,
    handleRefresh: handleRefresh,
    dataTableData: {
      columns: [
        { Header: "First Name", accessor: "first_name" },
        { Header: "Email", accessor: "email" },
        { Header: "Business Name", accessor: "business_name" },
        { Header: "Action", accessor: "action" },
      ],

      rows: List.map((data) => ({
        first_name: data.first_name,
        email: data.email,
        business_name: data.business_name,
        action: (
          <SoftBox>
            <EditModal products={products} apps={apps} data={data} handleRefresh={handleRefresh} />
            <SoftButton
              variant="contained"
              color="error"
              size="small"
              sx={{ marginLeft: 2 }}
              onClick={() => handleDelete(data._id)}
            >
              Delete
            </SoftButton>
            <Link  to={"../account-detail"}>
            <SoftButton
              variant="contained"
              color="info"
              size="small"
              sx={{ marginLeft: 2 }}
            >
              View
            </SoftButton>
            </Link>
          </SoftBox>
        ),
      })),
    },
  };
}
